import React, { useState, useMemo, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import NavigationSidebar from '../../components/navigation/NavigationSidebar';
import UserProfileDropdown from '../../components/navigation/UserProfileDropdown';
import NotificationCenter from '../../components/navigation/NotificationCenter';
import BreadcrumbNavigation from '../../components/navigation/BreadcrumbNavigation';
import FinancialSummaryCards from './components/FinancialSummaryCards';
import TransactionFilters from './components/TransactionFilters';
import TransactionsTable from './components/TransactionsTable';
import PaymentProcessingModal from './components/PaymentProcessingModal';
import GroupProfitabilityPanel from './components/GroupProfitabilityPanel';
import ProfitabilityChart from './components/ProfitabilityChart';
import AuditTrailPanel from './components/AuditTrailPanel';
import QuickActionsPanel from './components/QuickActionsPanel';
import CostEntryModal from './components/CostEntryModal';
import BulkCostImportModal from './components/BulkCostImportModal';
import CostTemplatesPanel from './components/CostTemplatesPanel';
import ExpenseApprovalPanel from './components/ExpenseApprovalPanel';
import BudgetAnalysisPanel from './components/BudgetAnalysisPanel';
import ROIDashboard from './components/ROIDashboard';
import GroupROIRankings from './components/GroupROIRankings';
import BreakevenAnalysisPanel from './components/BreakevenAnalysisPanel';
import Button from '../../components/ui/Button';
import { getTransactions, getGroups, createBulkCostEntries, getROIAnalysisData } from '../../services/supabaseService';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { exportTransactionsToPDF, exportTransactionsToCSV, exportProfitabilityToPDF, exportROIAnalysisToPDF, exportROIAnalysisToCSV } from '../../utils/exportUtils';
import Icon from '../../components/AppIcon';




const FinancialManagement = () => {
  const navigate = useNavigate();
  const { currentLanguage, t } = useLanguage();
  const { profile, loading: authLoading } = useAuth();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isCostEntryModalOpen, setIsCostEntryModalOpen] = useState(false);
  const [isBulkImportModalOpen, setIsBulkImportModalOpen] = useState(false);
  const [selectedPilgrim, setSelectedPilgrim] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterGroup, setFilterGroup] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [transactions, setTransactions] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [roiData, setRoiData] = useState(null);
  const [showROIAnalysis, setShowROIAnalysis] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [transactionsData, groupsData, roiAnalysisData] = await Promise.all([
        getTransactions(),
        getGroups(),
        getROIAnalysisData()
      ]);
      setTransactions(transactionsData || []);
      setGroups(groupsData || []);
      setRoiData(roiAnalysisData || null);
    } catch (err) {
      console.error('Error loading financial data:', err);
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const mockUser = profile || {
    name: 'Ahmed Benali',
    role: 'accountant',
    agency: 'Omra Voyages Maroc',
    avatar: 'https://randomuser.me/api/portraits/men/45.jpg'
  };

  const mockNotifications = [];

  const summaryData = useMemo(() => {
    return [
      {
        labelKey: 'totalRevenue',
        amount: transactions?.filter(t => t?.status === 'completed')?.reduce((sum, t) => sum + (parseFloat(t?.amount) || 0), 0) || 0,
        trend: 12.5,
        isPositiveTrend: true,
        comparisonKey: 'vsLastMonth',
        icon: 'TrendingUp',
        iconColor: 'text-success',
        bgColor: 'bg-success/10'
      },
      {
        labelKey: 'outstandingBalance',
        amount: transactions?.filter(t => t?.status === 'pending' || t?.status === 'overdue')?.reduce((sum, t) => sum + (parseFloat(t?.amount) || 0), 0) || 0,
        trend: -8.3,
        isPositiveTrend: true,
        comparisonKey: 'vsLastMonth',
        icon: 'Clock',
        iconColor: 'text-warning',
        bgColor: 'bg-warning/10'
      },
      {
        labelKey: 'refundsProcessing',
        amount: transactions?.filter(t => t?.status === 'refunded')?.reduce((sum, t) => sum + (parseFloat(t?.amount) || 0), 0) || 0,
        trend: 15.2,
        isPositiveTrend: false,
        comparisonKey: 'vsLastQuarter',
        icon: 'ArrowLeftRight',
        iconColor: 'text-primary',
        bgColor: 'bg-primary/10'
      },
      {
        labelKey: 'groupProfitability',
        amount: groups?.reduce((sum, g) => sum + (parseFloat(g?.revenue) || 0), 0) || 0,
        trend: 18.7,
        isPositiveTrend: true,
        comparisonKey: 'vsLastQuarter',
        icon: 'TrendingUp',
        iconColor: 'text-success',
        bgColor: 'bg-success/10'
      }
    ];
  }, [transactions, groups]);

  const filteredTransactions = useMemo(() => {
    return transactions?.filter((transaction) => {
      const matchesStatus = filterStatus === 'all' || transaction?.status === filterStatus;
      const matchesType = filterType === 'all' || transaction?.type === filterType;
      const matchesGroup = filterGroup === 'all' || transaction?.group === filterGroup;
      const matchesSearch = transaction?.pilgrimName?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                           transaction?.id?.toLowerCase()?.includes(searchQuery?.toLowerCase());
      
      let matchesDateRange = true;
      if (dateRange?.start && dateRange?.end) {
        const transactionDate = transaction?.transactionDate || transaction?.dueDate;
        if (transactionDate) {
          matchesDateRange = transactionDate >= dateRange?.start && transactionDate <= dateRange?.end;
        }
      }
      
      return matchesStatus && matchesType && matchesGroup && matchesSearch && matchesDateRange;
    });
  }, [transactions, filterStatus, filterType, filterGroup, searchQuery, dateRange]);

  const handleFilterChange = (key, value) => {
    if (key === 'status') {
      setFilterStatus(value);
    } else if (key === 'transactionType') {
      setFilterType(value);
    } else if (key === 'group') {
      setFilterGroup(value);
    } else if (key === 'search') {
      setSearchQuery(value);
    } else if (key === 'dateRange') {
      setDateRange(value);
    }
  };

  const handleResetFilters = () => {
    setFilterStatus('all');
    setFilterType('all');
    setFilterGroup('all');
    setSearchQuery('');
    setDateRange({ start: '', end: '' });
  };

  const handleViewDetails = (transaction) => {
    console.log('View transaction details:', transaction);
  };

  const handleGenerateInvoice = (transaction) => {
    console.log('Generate invoice for:', transaction);
  };

  const handleQuickAction = (actionKey) => {
    if (actionKey === 'processPayment') {
      setSelectedPilgrim({ name: 'Mohamed Alami', id: 'PIL-2026-045' });
      setIsPaymentModalOpen(true);
    } else if (actionKey === 'addCost') {
      setIsCostEntryModalOpen(true);
    } else if (actionKey === 'bulkImport') {
      setIsBulkImportModalOpen(true);
    } else if (actionKey === 'generateInvoice') {
      // Generate invoice for the first pending transaction or show selection
      const pendingTransaction = transactions?.find(t => t?.status === 'pending');
      if (pendingTransaction) {
        handleGenerateInvoice(pendingTransaction);
        alert(`${t('financial.invoiceGenerated')} ${pendingTransaction?.pilgrimName}`);
      } else {
        alert(t('financial.noPendingTransactions'));
      }
    } else if (actionKey === 'processRefund') {
      // Process refund for completed transactions
      const completedTransactions = transactions?.filter(t => t?.status === 'completed');
      if (completedTransactions?.length > 0) {
        const refundAmount = prompt(t('financial.enterRefundAmount'));
        if (refundAmount && !isNaN(refundAmount)) {
          alert(`${t('financial.refundProcessing')} ${refundAmount} ${t('financial.madProcessing')}`);
          loadData(); // Reload data after refund
        }
      } else {
        alert(t('financial.noCompletedTransactions'));
      }
    } else if (actionKey === 'exportAccounting') {
      // Export accounting data
      handleExportTransactionsCSV();
      alert(t('financial.accountingExported'));
    } else if (actionKey === 'generateContract') {
      // Navigate to document management for contract generation
      navigate('/document-management');
    } else if (actionKey === 'viewReports') {
      // Navigate to analytics dashboard
      navigate('/analytics-dashboard');
    } else {
      console.log('Quick action:', actionKey);
    }
  };

  const handleApplyTemplate = async (template) => {
    if (!template?.templateData || template?.templateData?.length === 0) {
      alert(t('financial.emptyTemplate'));
      return;
    }

    const groupId = prompt(t('financial.enterGroupId'));

    if (!groupId) return;

    try {
      const costEntries = template?.templateData?.map(item => ({
        ...item,
        groupId,
        entryDate: new Date()?.toISOString()?.split('T')?.[0]
      }));

      await createBulkCostEntries(costEntries);
      
      alert(t('financial.templateAppliedSuccess'));
      
      loadData();
    } catch (error) {
      console.error('Error applying template:', error);
      alert(t('financial.errorApplying'));
    }
  };

  const handleExportProfitability = () => {
    exportProfitabilityToPDF(groups, currentLanguage);
  };

  const handleExportTransactionsPDF = () => {
    exportTransactionsToPDF(filteredTransactions, currentLanguage);
  };

  const handleExportTransactionsCSV = () => {
    exportTransactionsToCSV(filteredTransactions);
  };

  const handleExportROIAnalysisPDF = () => {
    if (roiData) {
      exportROIAnalysisToPDF(roiData, currentLanguage);
    }
  };

  const handleExportROIAnalysisCSV = () => {
    if (roiData) {
      exportROIAnalysisToCSV(roiData);
    }
  };

  const handleLogout = () => {
    console.log('Logout');
  };

  return (
    <div className="min-h-screen bg-background">
      <NavigationSidebar
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
      />
      <div
        className={`transition-smooth ${
          isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-72'
        }`}
      >
        <header className="sticky top-0 z-30 bg-card border-b border-border shadow-warm">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0 pl-16 pr-4 md:px-6 lg:px-8 py-4">
            <div className="flex-1">
              <h1 className="text-xl md:text-2xl lg:text-3xl font-semibold text-foreground">
                {t('financial.title')}
              </h1>
              <p className="caption text-muted-foreground mt-1">
                {t('financial.subtitle')}
              </p>
            </div>

            <div className="flex items-center gap-2 sm:gap-3">
              <NotificationCenter
                notifications={mockNotifications}
                currentLanguage={currentLanguage}
                onNotificationClick={(notification) => console.log('Notification clicked:', notification)}
                onMarkAsRead={(notificationId) => console.log('Mark as read:', notificationId)}
              />
              <UserProfileDropdown
                user={mockUser}
                onLogout={handleLogout}
                currentLanguage={currentLanguage}
                onLanguageChange={(lang) => console.log('Language changed:', lang)}
              />
            </div>
          </div>
        </header>

        <main className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
          <BreadcrumbNavigation currentLanguage={currentLanguage} />

          <div className="space-y-6 md:space-y-8">
            <FinancialSummaryCards
              summaryData={summaryData}
              currentLanguage={currentLanguage}
            />

            <QuickActionsPanel onAction={handleQuickAction} />

            {/* ROI Analysis Toggle */}
            <div className="bg-gradient-to-r from-primary/10 to-success/10 rounded-lg p-4 md:p-6 border border-primary/20">
              <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
                <div className="flex items-start gap-3 flex-1">
                  <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
                    <Icon name="TrendingUp" size={24} className="text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-foreground">
                      {t('financial.roiAnalysisTitle')}
                    </h3>
                    <p className="caption text-muted-foreground mt-1">
                      {t('financial.roiAnalysisDescription')}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2 w-full lg:w-auto">
                  {showROIAnalysis && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="FileText"
                        iconPosition="left"
                        onClick={handleExportROIAnalysisPDF}
                        className="flex-1 sm:flex-none"
                      >
                        {t('financial.exportPDF')}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="Download"
                        iconPosition="left"
                        onClick={handleExportROIAnalysisCSV}
                        className="flex-1 sm:flex-none"
                      >
                        {t('financial.exportCSV')}
                      </Button>
                    </>
                  )}
                  <Button
                    variant={showROIAnalysis ? 'default' : 'outline'}
                    iconName={showROIAnalysis ? 'ChevronUp' : 'ChevronDown'}
                    iconPosition="right"
                    onClick={() => setShowROIAnalysis(!showROIAnalysis)}
                    className="flex-1 sm:flex-none"
                  >
                    {showROIAnalysis 
                      ? t('financial.hide')
                      : t('financial.showAnalysis')}
                  </Button>
                </div>
              </div>
            </div>

            {/* ROI Analysis Section */}
            {showROIAnalysis && (
              <div className="space-y-6">
                <ROIDashboard roiData={roiData} loading={loading} />
                
                <GroupROIRankings roiData={roiData} loading={loading} />
                
                <BreakevenAnalysisPanel roiData={roiData} loading={loading} />
              </div>
            )}

            {/* Cost Management Section */}
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <h2 className="text-xl font-semibold text-foreground">
                  {t('financial.costEntry')}
                </h2>
                <div className="flex flex-wrap gap-3 w-full sm:w-auto">
                  <Button
                    variant="outline"
                    iconName="Upload"
                    iconPosition="left"
                    onClick={() => setIsBulkImportModalOpen(true)}
                    className="flex-1 sm:flex-none"
                  >
                    {t('financial.bulkCostImport')}
                  </Button>
                  <Button
                    variant="default"
                    iconName="Plus"
                    iconPosition="left"
                    onClick={() => setIsCostEntryModalOpen(true)}
                    className="flex-1 sm:flex-none"
                  >
                    {t('financial.addCostEntry')}
                  </Button>
                </div>
              </div>

              <CostTemplatesPanel onApplyTemplate={handleApplyTemplate} />
              
              <ExpenseApprovalPanel onApprovalChange={loadData} />
              
              <BudgetAnalysisPanel />
            </div>

            <TransactionFilters
              filters={{
                status: filterStatus,
                transactionType: filterType,
                group: filterGroup,
                search: searchQuery,
                dateRange: dateRange
              }}
              onFilterChange={handleFilterChange}
              onReset={handleResetFilters}
              onApply={handleFilterChange}
              currentLanguage={currentLanguage}
            />

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
              <h3 className="text-lg font-semibold text-foreground">
                {t('financial.transactions')}
              </h3>
              <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="FileText"
                  iconPosition="left"
                  onClick={handleExportTransactionsPDF}
                  className="flex-1 sm:flex-none"
                >
                  {t('financial.exportPDF')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Download"
                  iconPosition="left"
                  onClick={handleExportTransactionsCSV}
                  className="flex-1 sm:flex-none"
                >
                  {t('financial.exportCSV')}
                </Button>
              </div>
            </div>

            <TransactionsTable
              transactions={filteredTransactions}
              currentLanguage={currentLanguage}
              onViewDetails={handleViewDetails}
              onGenerateInvoice={handleGenerateInvoice}
            />

            <ProfitabilityChart groups={groups} />

            <GroupProfitabilityPanel
              groups={groups}
              onExport={handleExportProfitability}
            />

            <AuditTrailPanel />
          </div>
        </main>
      </div>
      <PaymentProcessingModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        pilgrim={selectedPilgrim}
      />

      <CostEntryModal
        isOpen={isCostEntryModalOpen}
        onClose={() => setIsCostEntryModalOpen(false)}
        onSuccess={loadData}
      />

      <BulkCostImportModal
        isOpen={isBulkImportModalOpen}
        onClose={() => setIsBulkImportModalOpen(false)}
        onSuccess={loadData}
      />
    </div>
  );
};

export default FinancialManagement;